import { Download, Loader2, Maximize2, Pause, PenLine, Play, RefreshCw } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import type { Persona } from "@/componentes/personas/persona"
import { Modal } from "@/componentes/ui/Modal"
import { FIRMA } from "@/lib/firma"
import { componerTelefono, PAIS_POR_DEFECTO, TELEFONO_CORPORATIVO } from "@/lib/paises"

/**
 * La firma de correo corporativa de una persona.
 *
 * El motor está portado tal cual desde `setieca_procesos` — misma tarjeta, misma
 * animación y el mismo GIF de «25 años». Aquí sólo se le dan los datos de la ficha
 * y se ofrece la vista previa y las descargas.
 *
 * ⚠ **Nada se prepara hasta que alguien la pide.** `preparar()` carga las fuentes y
 * un GIF de 2,3 MB: hacerlo al abrir la ficha cobraría esa descarga a todo el que
 * entra a mirar un teléfono. La sección nace plegada y el motor arranca al abrirla.
 *
 * ⚠ **El correo que sale impreso es el CORPORATIVO, el de la cuenta enlazada.** El
 * `email_personal` de la ficha es privado y no tiene por qué acabar en una tarjeta
 * que se manda a clientes. Una ficha sin cuenta no tiene correo corporativo, y
 * entonces no hay firma que generar: se dice, no se inventa uno.
 *
 * ⚠ **La vista previa es el fotograma FINAL, no el primero.** Es el estado en que
 * queda la tarjeta cuando termina la animación, o sea lo que verá quien reciba el
 * correo si su cliente no reproduce el GIF. Enseñar el primero mostraría un logo a
 * medio entrar y parecería que algo se rompió.
 */

type Estado = "plegado" | "preparando" | "listo" | "error"

export function FirmaDePersona({
  persona,
  haycambiosSinGuardar = false,
}: {
  persona: Persona
  /** ⚠ Se genera con lo GUARDADO, no con lo escrito en el formulario. Sin decirlo,
   *  quien teclea el teléfono y ve la firma sin cambiar concluye que está rota. */
  haycambiosSinGuardar?: boolean
}) {
  const lienzo = useRef<HTMLCanvasElement | null>(null)
  const [estado, setEstado] = useState<Estado>("plegado")
  const [reproduciendo, setReproduciendo] = useState(false)
  const [bajando, setBajando] = useState<"png" | "gif" | null>(null)
  const [fallo, setFallo] = useState<string | null>(null)
  const [enGrande, setEnGrande] = useState(false)

  const nombre = `${persona.nombres} ${persona.apellidos}`.trim()
  const correo = persona.email_corporativo ?? null

  /**
   * ⚠ **Un solo formato para todo el mundo.** El prefijo lo pone el selector de la
   * ficha y el número se normaliza a espacios simples; sin eso salen «0241-8233560»,
   * «(241) 823.35.60» y «+58 241 8233560» en tres firmas de la misma agencia.
   *
   * Sin número propio se usa el de la empresa —hoy una constante; cuando exista el
   * módulo de configuración de empresa, saldrá de ahí—.
   */
  const propio = (persona.telefono_corporativo ?? "").trim()
  const telefono = componerTelefono(
    propio === "" ? PAIS_POR_DEFECTO : persona.pais_telefono || PAIS_POR_DEFECTO,
    propio === "" ? TELEFONO_CORPORATIVO : propio,
  )

  /** Vuelca los datos de la ficha en el motor y repinta el fotograma final. */
  const repintar = useCallback(() => {
    FIRMA.setTrabajador({
      nombre,
      cargo: persona.cargo ?? "",
      // ⚠ **El corporativo de la persona; si no tiene, el de la empresa.**
      // `persona.telefono` es el PARTICULAR —vive junto al «correo personal»— y esta
      // tarjeta se pega al pie de correos que van a clientes: el móvil de alguien no
      // tiene por qué acabar ahí. Es la misma decisión que ya se tomó con el correo.
      telefono,
      email: correo ?? "",
    })
    if (!reproduciendo) FIRMA.pintarFinal(lienzo.current)
  }, [nombre, persona.cargo, correo, telefono, reproduciendo])

  async function abrir() {
    setEstado("preparando")
    setFallo(null)
    try {
      await FIRMA.preparar()
      // El logo animado es el mismo recurso del original. El resto de la marca
      // —RIF, teléfonos, dirección, eslogan— ya viene por defecto en el motor.
      await FIRMA.setEmpresa({
        emp_logo: "/firma/secuencia-25-anos-setieca.gif",
        emp_logo25: "/firma/secuencia-25-anos-setieca.gif",
      })
      setEstado("listo")
    } catch (e) {
      setFallo(e instanceof Error ? e.message : "No se pudo preparar la firma")
      setEstado("error")
    }
  }

  // Al quedar listo —y cada vez que cambian los datos de la ficha— se repinta.
  useEffect(() => {
    if (estado === "listo") repintar()
  }, [estado, repintar])

  /**
   * ⚠ **Detener cuando el lienzo se va, con la limpieza del ref y no con un efecto.**
   *
   * `reproducir()` deja vivo un bucle de `requestAnimationFrame`; cerrar el cajón sin
   * pararlo lo deja dibujando sobre un canvas que ya no está en el documento, gastando
   * batería por una pantalla que nadie mira.
   *
   * El primer intento fue un efecto con la lista de dependencias vacía, y no
   * funcionaba: corría una vez al montar, cuando la sección todavía está plegada y el
   * canvas no existe, así que la limpieza se quedaba con un `null` para siempre. Lo
   * cazó la prueba de desmontaje. Ponerle `[estado]` lo arreglaba pero mentía —el
   * cuerpo no lee `estado`, sólo lo usaba para re-vincularse— y el linter lo marcaba
   * con razón. React 19 admite que un ref de callback devuelva su limpieza, que es
   * exactamente lo que hace falta: se ata a ESTE nodo y se suelta cuando ESTE nodo se
   * va, sin inventarse dependencias.
   */
  const atarLienzo = useCallback((nodo: HTMLCanvasElement) => {
    lienzo.current = nodo
    return () => {
      FIRMA.detener(nodo)
      lienzo.current = null
    }
  }, [])

  function alternarReproduccion() {
    const cv = lienzo.current
    if (cv === null) return
    if (reproduciendo) {
      FIRMA.detener(cv)
      FIRMA.pintarFinal(cv)
    } else {
      FIRMA.reproducir(cv)
    }
    setReproduciendo(!reproduciendo)
  }

  async function descargar(formato: "png" | "gif") {
    setBajando(formato)
    setFallo(null)
    try {
      // ⚠ **Las mismas escalas que `setieca_procesos`: PNG a 2x y GIF a 1x.** El
      // lienzo mide 1374×462, así que el PNG sale a 2748×924 — que es lo que hace
      // que la firma se vea nítida en una pantalla de retina en vez de borrosa. El
      // GIF se queda en 1x porque a 2x el archivo se dispara y hay clientes de
      // correo que lo rechazan por peso.
      const blob = formato === "png" ? await FIRMA.png(2) : await FIRMA.gif({ escala: 1, fps: 12 })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `firma-${nombre.toLowerCase().replace(/\s+/g, "-")}.${formato}`
      a.click()
      // Se revoca en el siguiente turno: revocarlo en la misma línea a veces
      // cancela la descarga antes de que el navegador la haya empezado.
      setTimeout(() => URL.revokeObjectURL(url), 0)
    } catch (e) {
      setFallo(e instanceof Error ? e.message : "No se pudo exportar")
    } finally {
      setBajando(null)
    }
  }

  if (persona.tipo === "CLIENTE") return null

  return (
    <section>
      <h3 className="rotulo mb-3">Firma de correo</h3>

      {/* ⚠ **La firma se genera con lo GUARDADO.** Es lo correcto —una firma sacada
          de un formulario a medio llenar puede no coincidir con la ficha— pero sin
          decirlo, quien acaba de teclear el teléfono corporativo ve la tarjeta con el
          de la empresa y concluye que no funciona. Pasó. */}
      {haycambiosSinGuardar && (
        <p
          role="status"
          className="mb-3 superficie p-2 text-xs"
          style={{ borderColor: "var(--primary-borde)", color: "var(--fg-muted)" }}
          data-testid="firma-desactualizada"
        >
          Hay cambios sin guardar en la ficha. La firma se genera con lo guardado: pulsa{" "}
          <strong>Guardar cambios</strong> y se actualiza sola.
        </p>
      )}

      {correo === null ? (
        <p className="superficie p-3 text-sm" style={{ color: "var(--fg-muted)" }}>
          Esta ficha no tiene cuenta enlazada, así que no hay correo corporativo que imprimir.
          Enlázala arriba, en «Acceso al sistema», y la firma se podrá generar.
        </p>
      ) : estado === "plegado" ? (
        <div className="superficie p-3">
          <p className="mb-3 text-sm" style={{ color: "var(--fg-muted)" }}>
            Se genera con el nombre, el cargo, el teléfono de la ficha y <strong>{correo}</strong>.
          </p>
          <button
            type="button"
            onClick={abrir}
            data-testid="abrir-firma"
            className="boton-secundario h-9 px-3 text-sm"
          >
            <PenLine size={14} aria-hidden={true} />
            Ver la firma
          </button>
          <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
            Descarga unos megas de tipografías y del logo animado, así que sólo se carga cuando lo
            pides.
          </p>
        </div>
      ) : (
        <div>
          {estado === "preparando" && (
            <p
              role="status"
              className="flex items-center gap-2 text-sm"
              style={{ color: "var(--fg-muted)" }}
            >
              <Loader2 size={16} aria-hidden={true} className="animate-spin" />
              Cargando la tarjeta…
            </p>
          )}

          {/* El lienzo es de 1374×462 y se enseña a lo ancho que haya: el tamaño de
              dibujo NO cambia, sólo el de presentación, para que la exportación
              salga siempre a resolución completa. */}
          <canvas
            ref={atarLienzo}
            width={FIRMA.ANCHO}
            height={FIRMA.ALTO}
            data-testid="lienzo-firma"
            aria-label={`Vista previa de la firma de ${nombre}`}
            className="w-full rounded-[var(--radius-tarjeta)]"
            style={{
              border: "1px solid var(--border)",
              display: estado === "listo" ? "block" : "none",
            }}
          />

          {estado === "listo" && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={alternarReproduccion}
                data-testid="reproducir-firma"
                className="boton-secundario h-8 px-2 text-xs"
              >
                {reproduciendo ? (
                  <Pause size={14} aria-hidden={true} />
                ) : (
                  <Play size={14} aria-hidden={true} />
                )}
                {reproduciendo ? "Detener" : "Ver la animación"}
              </button>
              <button
                type="button"
                disabled={bajando !== null}
                onClick={() => descargar("png")}
                data-testid="descargar-png"
                className="boton-primario h-8 px-2 text-xs"
              >
                <Download size={14} aria-hidden={true} />
                {bajando === "png" ? "Generando…" : "Descargar PNG"}
              </button>
              <button
                type="button"
                disabled={bajando !== null}
                onClick={() => descargar("gif")}
                data-testid="descargar-gif"
                className="boton-secundario h-8 px-2 text-xs"
              >
                <Download size={14} aria-hidden={true} />
                {bajando === "gif" ? "Generando…" : "Descargar GIF animado"}
              </button>
              {/* ⚠ **Regenerar existe aunque la vista previa ya se actualice sola.**
                  Se repinta cuando cambian los datos de la ficha, pero quien acaba de
                  corregir el cargo y guardar no tiene forma de SABER que lo que ve es
                  lo nuevo. Un botón que lo confirma cuesta una línea y evita que
                  alguien mande una firma vieja creyendo que la cambió. */}
              <button
                type="button"
                onClick={repintar}
                data-testid="regenerar-firma"
                className="boton-secundario h-8 px-2 text-xs"
              >
                <RefreshCw size={14} aria-hidden={true} />
                Regenerar
              </button>
              {/* La ficha vive en un cajón de 560 px y la tarjeta mide 1374: aquí se
                  ve en pequeño por fuerza. Esto la abre a tamaño de lectura. */}
              <button
                type="button"
                onClick={() => setEnGrande(true)}
                aria-haspopup="dialog"
                data-testid="firma-en-grande"
                className="boton-secundario h-8 px-2 text-xs"
              >
                <Maximize2 size={14} aria-hidden={true} />
                Ver en grande
              </button>
            </div>
          )}

          {estado === "listo" && (
            <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
              El PNG es la versión quieta y pesa poco: es la que aguantan todos los clientes de
              correo. El GIF lleva la animación, pesa bastante más y Outlook de escritorio lo pinta
              quieto de todos modos.
            </p>
          )}

          {fallo !== null && (
            <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
              {fallo}
            </p>
          )}
        </div>
      )}

      {/* ⚠ **Un lienzo APARTE, no el mismo movido de sitio.** El de arriba lo tiene
          atado el ref del que cuelga la parada de la animación; sacarlo y devolverlo
          lo desmontaría y remontaría cada vez que se abre el modal. Aquí basta con
          pintar el fotograma final una vez. */}
      <Modal
        abierto={enGrande}
        titulo="Firma de correo"
        descripcion={`${nombre} · ${correo ?? ""}`}
        ancho="lg"
        onCerrar={() => setEnGrande(false)}
      >
        <canvas
          ref={(nodo) => {
            if (nodo !== null) FIRMA.pintarFinal(nodo)
          }}
          width={FIRMA.ANCHO}
          height={FIRMA.ALTO}
          data-testid="lienzo-firma-grande"
          aria-label={`Firma de ${nombre}, a tamaño completo`}
          className="w-full rounded-[var(--radius-tarjeta)]"
          style={{ border: "1px solid var(--border)" }}
        />
      </Modal>
    </section>
  )
}
